import type { AreaData } from '@revelio/replots';

import type { SheetData } from '../types';

export type MixedStatsData = {
  headcount: AreaData | null;
  growthRate: AreaData | null;
  postings: AreaData | null;
  sentimentOverall: AreaData | null;
  sentimentBusinessOutlook: AreaData | null;
  sentimentCompensation: AreaData | null;
};

/**
 * Convert MixedStatsData to sheet format
 * Used for mixed-stats charts that show 6 stat-trend charts
 */
export const convertMixedStatsDataToSheet = ({
  data,
}: {
  data: MixedStatsData;
  primaryDimension: string;
  secondaryDimension: string;
  metric: string;
}): SheetData[] => {
  const allDates = new Set<string>();

  // Collect all unique dates from all series
  Object.values(data).forEach((series) => {
    if (series?.values) {
      series.values.forEach((point) => {
        if (point.x) {
          allDates.add(String(point.x));
        }
      });
    }
  });

  const sortedDates = Array.from(allDates).sort();

  const seriesConfig: Array<{ key: keyof MixedStatsData; name: string }> = [
    { key: 'headcount', name: 'Headcount' },
    { key: 'growthRate', name: 'Growth Rate' },
    { key: 'postings', name: 'Active Postings' },
    { key: 'sentimentOverall', name: 'Sentiment Overall' },
    { key: 'sentimentBusinessOutlook', name: 'Business Outlook' },
    { key: 'sentimentCompensation', name: 'Compensation Benefits' },
  ];

  return sortedDates.map((date) => {
    const row: Record<string, string | number> = {
      Date: date,
    };

    // Add each series data via configuration
    seriesConfig.forEach(({ key, name }) => {
      const series = data[key];
      if (!series?.values) return;
      const point = series.values.find((p) => String(p.x) === date);
      if (point && point.value !== undefined && point.value !== null) {
        row[name] = point.value;
      }
    });

    return row;
  });
};
